import { useEffect, useState } from 'react';
import type { ApiClient } from '@sentinel/api-client';

/**
 * `pending` — not known yet, or the dialog is closed. Offer nothing.
 * `available` — offer the WebAuthn method.
 * `unavailable` — do not offer it.
 */
export type WebAuthnAvailability = 'pending' | 'available' | 'unavailable';

/** Whether this browser can run an assertion ceremony at all. */
export function browserSupportsWebAuthn(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.get === 'function'
  );
}

/**
 * Decides whether the step-up dialog should offer WebAuthn.
 *
 * Reads the user's registered credentials once per open. It deliberately fails
 * OPEN: if the list cannot be read we still offer the method, because hiding it
 * on a transport error would re-create the dead end this exists to remove — a
 * user whose only second factor is a passkey would be left with a TOTP field
 * they cannot fill. A browser with no WebAuthn support is the one case where
 * hiding it is right, since the ceremony could not run.
 */
export function useWebAuthnAvailability(
  apiClient: ApiClient,
  open: boolean,
): WebAuthnAvailability {
  const [availability, setAvailability] = useState<WebAuthnAvailability>('pending');

  useEffect(() => {
    if (!open) {
      setAvailability('pending');
      return;
    }
    if (!browserSupportsWebAuthn()) {
      setAvailability('unavailable');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.GET('/auth/webauthn/credentials', {});
        if (cancelled) return;
        const items = (res.data as { items?: unknown[] } | undefined)?.items;
        if (res.error || !Array.isArray(items)) {
          setAvailability('available');
          return;
        }
        setAvailability(items.length > 0 ? 'available' : 'unavailable');
      } catch {
        if (!cancelled) setAvailability('available');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient, open]);

  return availability;
}
